import { DEF } from '../constants/defaults';
import { FF } from '../utils/format';
import { Card } from '../components/Card';
import { Stat } from '../components/Stat';
import { Heading } from '../components/Heading';
import { Slider } from '../components/Slider';
import { Toggle } from '../components/Toggle';
import { Grid } from '../components/Grid';
import { Stack } from '../components/Stack';
import { Home, Briefcase, TrendingUp, Building2, PiggyBank, Download, Upload, RotateCcw } from '../components/icons';
import { useApp } from '../hooks/useApp';
import GamificationSettings from '../features/settings/GamificationSettings';

export default function SettingsTab() {
  const { s, c, proj, pF, p0, up, upM, setS, tt, fiAge, fiReached, coastAge, budgetMonthly, blendedReturn, realReturn, amor, scen, mcData, mcRunning, runMonteCarlo, showToast, setConfirm, fileRef } = useApp();
  const ActionBtn = ({ children, fn, danger }) => (
    <button onClick={fn} style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "8px 18px", borderRadius: 20, fontSize: 13,
      fontWeight: 600, cursor: "pointer",
      border: `1px solid ${danger ? c.red : c.border}`,
      background: "transparent", color: danger ? c.red : c.fg,
    }}>{children}</button>
  );

  return (
    <Stack>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <Stat l="Configuration" v="Core Assumptions" c={c} />
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <ActionBtn fn={() => {
            const b = new Blob([JSON.stringify(s, null, 2)], { type: "application/json" });
            const u = URL.createObjectURL(b);
            const a = document.createElement("a");
            a.href = u; a.download = "wealthmaxxing.json"; a.click();
            URL.revokeObjectURL(u);
            showToast("Exported successfully", "success");
          }}>
            <Download size={14} /> Export
          </ActionBtn>
          <ActionBtn fn={() => fileRef.current?.click()}>
            <Upload size={14} /> Import
          </ActionBtn>
          <input ref={fileRef} type="file" accept=".json" style={{ display: "none" }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              const r = new FileReader();
              r.onload = () => {
                try {
                  const parsed = JSON.parse(r.result);
                  if (typeof parsed !== "object" || parsed === null) throw new Error("Invalid format");
                  if (parsed.age && (typeof parsed.age !== "number" || parsed.age < 16 || parsed.age > 80)) throw new Error("Invalid age");
                  if (parsed.salary && (typeof parsed.salary !== "number" || parsed.salary < 0)) throw new Error("Invalid salary");
                  setS({ ...DEF, ...parsed });
                  showToast("Imported successfully", "success");
                } catch (err) {
                  showToast(`Import failed: ${err.message || "invalid file"}`, "error");
                }
              };
              r.readAsText(f);
              e.target.value = "";
            }} />
          <ActionBtn danger fn={() => setConfirm({
            msg: "This will reset all settings, scenarios, and tracked actuals to defaults. This cannot be undone.",
            onOk: () => { setS(DEF); setConfirm(null); showToast("Reset to defaults", "info"); },
          })}>
            <RotateCcw size={14} /> Reset
          </ActionBtn>
        </div>
      </div>

      <Toggle l="Dark Mode" on={s.dark} fn={(v) => up("dark", v)} c={c} />
      <Toggle l="Sync Budget → Living Expense" on={s.budgetSync} fn={(v) => up("budgetSync", v)} c={c}
        sub={s.budgetSync ? `Projections use ${FF(budgetMonthly * 12)}/yr from budget` : "Projections use fixed living expense"} />

      <GamificationSettings />

      <Grid>
        <Card c={c} delay={0.05}>
          <Heading ic={Home} c={c}>Personal</Heading>
          <Slider l="Current Age" v={s.age} fn={(v) => up("age", v)} min={18} max={35} c={c} />
          <Slider l="Current Net Worth" v={s.nw0} fn={(v) => up("nw0", v)} min={0} max={200000} step={1000} f="$" c={c} />
          <Slider l="Career Start Age" v={s.startAge} fn={(v) => up("startAge", v)} min={22} max={28} c={c} />
          {!s.budgetSync && <Slider l="Living Expense Cap" v={s.liv} fn={(v) => up("liv", v)} min={20000} max={80000} step={1000} f="$" c={c} />}
          <Slider l="Inflation" v={s.inflation != null ? s.inflation : 0.025} fn={(v) => up("inflation", v)} min={0} max={0.06} step={0.005} f="%" c={c} />
          <Slider l="Plan To Age" v={s.maxAge || 40} fn={(v) => up("maxAge", v)} min={35} max={65} c={c} />
        </Card>
        <Card c={c} delay={0.08}>
          <Heading ic={Briefcase} c={c}>Career</Heading>
          <Slider l="Base Salary" v={s.salary} fn={(v) => up("salary", v)} min={60000} max={200000} step={5000} f="$" c={c} />
          <Slider l="Bonus Rate" v={s.bonusR} fn={(v) => up("bonusR", v)} min={0} max={0.3} step={0.01} f="%" c={c} />
          <Slider l="Salary Growth" v={s.salGr} fn={(v) => up("salGr", v)} min={0.02} max={0.12} step={0.005} f="%" c={c} />
        </Card>
        <Card c={c} delay={0.11}>
          <Heading ic={TrendingUp} c={c}>Investments</Heading>
          <Slider l="Return Rate" v={s.ret} fn={(v) => up("ret", v)} min={0.04} max={0.15} step={0.005} f="%" c={c} />
          <Slider l="TFSA Limit" v={s.tLim} fn={(v) => up("tLim", v)} min={5000} max={10000} step={500} f="$" c={c} />
          <Slider l="TFSA Starting Room" v={s.tfsaRoom || 95000} fn={(v) => up("tfsaRoom", v)} min={0} max={150000} step={5000} f="$" c={c} />
        </Card>
        <Card c={c} delay={0.14}>
          <Heading ic={Building2} c={c}>Real Estate</Heading>
          <Slider l="Purchase Price" v={s.hPrice} fn={(v) => upM({ hPrice: v, mortAmt: Math.max(0, v - s.down) })} min={400000} max={2500000} step={25000} f="$" c={c} />
          <Slider l="Down Payment" v={s.down} fn={(v) => upM({ down: Math.min(v, s.hPrice), mortAmt: Math.max(0, s.hPrice - Math.min(v, s.hPrice)) })} min={50000} max={Math.min(600000, s.hPrice)} step={10000} f="$" c={c} />
          <Slider l="Mortgage Rate" v={s.mortRate} fn={(v) => up("mortRate", v)} min={0.02} max={0.08} step={0.001} f="%" c={c} />
          <Slider l="Appreciation" v={s.hApp} fn={(v) => up("hApp", v)} min={0.01} max={0.08} step={0.005} f="%" c={c} />
          <Slider l="Extra Principal/yr" v={s.xPrin} fn={(v) => up("xPrin", v)} min={0} max={100000} step={5000} f="$" c={c} />
          <Slider l="Purchase Age" v={s.hAge} fn={(v) => up("hAge", v)} min={23} max={35} c={c} />
        </Card>
        <Card c={c} delay={0.17}>
          <Heading ic={PiggyBank} c={c}>Savings</Heading>
          <Slider l="22–25" v={s.s22} fn={(v) => up("s22", v)} min={0} max={80000} step={5000} f="$" c={c} />
          <Slider l="25–30" v={s.s25} fn={(v) => up("s25", v)} min={0} max={120000} step={5000} f="$" c={c} />
          <Slider l="30–35" v={s.s30} fn={(v) => up("s30", v)} min={0} max={150000} step={5000} f="$" c={c} />
          <Slider l="35–40" v={s.s35} fn={(v) => up("s35", v)} min={0} max={200000} step={5000} f="$" c={c} />
        </Card>
      </Grid>

      <Card c={c} delay={0.2}>
        <Heading c={c}>Track Actuals</Heading>
        <div style={{ fontSize: 13, color: c.muted, marginBottom: 16 }}>
          Enter your real NW at any age to see projected vs. actual on the Overview chart.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(100px, 1fr))", gap: 10 }}>
          {proj.map((d) => (
            <div key={d.a}>
              <div style={{ fontSize: 11, color: c.muted, marginBottom: 4, fontWeight: 500 }}>Age {d.a}</div>
              <input type="number" placeholder={FF(d.nw)}
                value={s.manNW?.[d.a] || ""}
                onChange={(e) => up("manNW", { ...s.manNW, [d.a]: e.target.value ? Number(e.target.value) : undefined })}
                style={{
                  width: "100%", padding: "6px 8px", borderRadius: 8,
                  border: `1px solid ${c.border}`, background: c.inputBg,
                  color: c.fg, fontSize: 13, fontVariantNumeric: "tabular-nums", outline: "none",
                }} />
            </div>
          ))}
        </div>
      </Card>
    </Stack>
  );
}
